import { useState, useCallback, useEffect } from 'react';
import { GAUtils } from '../model/GAUtils';
import { grantAdRewardCredit } from '../model/CreditUtils';
import { auth } from '../firebaseConfig';

/**
 * Google Ad Manager Rewarded Ad unit path.
 * Set via REACT_APP_GAM_REWARDED_AD_UNIT in .env, e.g.:
 *   REACT_APP_GAM_REWARDED_AD_UNIT=/21234567890/rewarded-ad
 */
const GAM_AD_UNIT = process.env.REACT_APP_GAM_REWARDED_AD_UNIT || '';

/**
 * Hook for displaying Google Ad Manager (GAM) Rewarded Ads and granting credits.
 *
 * Flow:
 * 1. Define a rewarded out-of-page slot via googletag
 * 2. Listen for rewardedSlotReady → makeRewardedVisible()
 * 3. Listen for rewardedSlotGranted → grant credit via backend
 * 4. Falls back to a confirmation dialog when GPT is unavailable (dev/testing)
 */
export const useRewardedAd = (
    onRewardEarned: () => void,
    onError?: (error: string) => void,
) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isReady, setIsReady] = useState(false);

    // Pre-check whether GPT is available
    useEffect(() => {
        const googletag = (window as any).googletag;
        if (googletag && googletag.cmd) {
            setIsReady(true);
        } else {
            // GPT not loaded — will use fallback
            setIsReady(false);
        }
    }, []);

    const showRewardedAd = useCallback(async () => {
        setIsLoading(true);

        try {
            GAUtils.trackRewardedAdClick();

            const googletag = (window as any).googletag;

            if (isReady && googletag?.apiReady && GAM_AD_UNIT) {
                // ---- Google Ad Manager Rewarded Ad ----
                googletag.cmd.push(() => {
                    const rewardedSlot = googletag.defineOutOfPageSlot(
                        GAM_AD_UNIT,
                        googletag.enums.OutOfPageFormat.REWARDED,
                    );

                    if (!rewardedSlot) {
                        console.warn('Failed to define rewarded slot, using fallback');
                        fallback();
                        return;
                    }

                    rewardedSlot.addService(googletag.pubads());

                    // Rewarded ad is ready to show
                    googletag.pubads().addEventListener(
                        'rewardedSlotReady',
                        (event: any) => {
                            // Show the ad to the user
                            event.makeRewardedVisible();
                        },
                    );

                    // User completed the ad → grant reward
                    googletag.pubads().addEventListener(
                        'rewardedSlotGranted',
                        async (_event: any) => {
                            GAUtils.trackRewardedAdCompleted('gam');
                            await grantCredit();
                        },
                    );

                    // User closed the ad before completion
                    googletag.pubads().addEventListener(
                        'rewardedSlotClosed',
                        () => {
                            // Destroy the slot so it can be re-created next time
                            googletag.destroySlots([rewardedSlot]);
                            setIsLoading(false);
                        },
                    );

                    // Slot failed to render (no fill, etc.)
                    googletag.pubads().addEventListener(
                        'slotRenderEnded',
                        (event: any) => {
                            if (event.slot === rewardedSlot && event.isEmpty) {
                                console.warn('Rewarded ad slot was empty (no fill)');
                                googletag.destroySlots([rewardedSlot]);
                                onError?.('No ad available right now. Please try again later.');
                                setIsLoading(false);
                            }
                        },
                    );

                    googletag.enableServices();
                    googletag.display(rewardedSlot);
                });
            } else {
                // ---- Fallback for dev/testing ----
                await fallback();
            }
        } catch (error: any) {
            console.error('Error showing rewarded ad:', error);
            GAUtils.trackRewardedAdError(error.message || 'Unknown error');
            onError?.(error.message || 'Failed to show ad. Please try again.');
            setIsLoading(false);
        }

        async function fallback() {
            console.warn('GAM Rewarded Ads not available, using fallback');
            const confirmed = window.confirm(
                'Watch a short ad to earn 1 free credit?\n\n' +
                '(In production, this will show a real rewarded ad via Google Ad Manager)',
            );
            if (!confirmed) {
                setIsLoading(false);
                return;
            }
            await new Promise(resolve => setTimeout(resolve, 2000));
            GAUtils.trackRewardedAdCompleted('fallback');
            await grantCredit();
        }

        async function grantCredit() {
            const user = auth.currentUser;
            if (!user) {
                onError?.('Please sign in to earn credits.');
                setIsLoading(false);
                return;
            }

            try {
                const result = await grantAdRewardCredit(user.uid);
                if (result.success) {
                    GAUtils.trackAdRewardCredited(result.balance);
                    window.dispatchEvent(new CustomEvent('credits-updated'));
                    onRewardEarned();
                }
            } catch (err: any) {
                console.error('Error granting ad reward credit:', err);
                onError?.(err.message || 'Failed to grant credit. Please try again.');
            }
            setIsLoading(false);
        }
    }, [isReady, onRewardEarned, onError]);

    return {
        showRewardedAd,
        isLoading,
    };
};
